<template>
  <header
    :class="[{ 'nav-collapsed': isSidebarCollapsed, 'nav-expanded': isSidebarHovered, 'header-top--onboarding': isOnboarding }]"
    class="header-top"
    header-theme="dark">
    <div
      v-if="isImpersonating"
      class="impersonation-banner">
      <i class="ik ik-alert-triangle" />
      <span>Acessando como <b>{{ organizationName }}</b> — as ações realizadas afetam a conta do cliente.</span>
    </div>
    <div class="container-fluid">
      <div class="d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center">
          <template v-if="isOnboarding">
            <img
              src="/static/images/logo_small.png"
              alt="Api4Com"
              class="onboarding-logo onboarding-logo--icon">
            <img
              src="/static/images/logo_only_name.png"
              alt="Api4Com"
              class="onboarding-logo onboarding-logo--name">
          </template>
          <button
            v-else
            type="button"
            title="Abrir menu"
            aria-label="Abrir menu"
            class="btn-icon mobile-nav-toggle d-lg-none"
            @click.stop="onMobileNavToggle">
            <i class="ik ik-menu" />
          </button>
        </div>
        <div v-if="!isOnboarding" class="d-flex align-items-center header-buttons">
          <charge-balance v-if="!isUnlimited" />
          <charge-new
            v-if="isAdmin && !isUnlimited"
            slider-name="chargeAmountSliderMenuHeader"
            class="ml-3" />
        </div>
        <div class="d-flex align-items-center ml-auto">
          <a
            v-if="!isOnboarding && referralLink"
            :href="referralLink"
            :title="referral.description"
            :style="{ backgroundColor: referral.backgroundColor, color: referral.textColor }"
            target="_blank"
            rel="noopener noreferrer"
            class="btn-indique-ganhe d-none d-md-inline-flex align-items-center mr-3">
            <i class="ik" :class="referralIconClass" />
            <span>{{ referral.title }}</span>
          </a>
          <div v-if="isOnboarding" class="d-flex align-items-center logged-user">
            <span>
              <small class="text-info">usuário:</small> <b>{{ userName }}</b>
              <small class="text-info">organização:</small> <b>{{ organizationName }}</b>
              <small class="text-info">acesso:</small> <b>{{ accessType }}</b>
            </span>
          </div>
          <div v-if="!isOnboarding" class="dropdown user-menu">
            <a
              id="userMenuToggle"
              href="#"
              class="d-flex align-items-center logged-user"
              data-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
              @click.prevent>
              <b class="user-menu__name" :title="userName">{{ userName }}</b>
              <i class="ik ik-chevron-down user-menu__caret" />
            </a>
            <div
              class="dropdown-menu dropdown-menu-right user-menu__list"
              aria-labelledby="userMenuToggle">
              <div class="user-menu__identity">
                <b :title="userName">{{ userName }}</b>
                <small>
                  <span class="user-menu__org" :title="organizationName">{{ organizationName }}</span>
                  <span class="user-menu__role">· {{ accessType }}</span>
                </small>
              </div>
              <div class="dropdown-divider" />
              <router-link
                class="dropdown-item"
                title="Meus dados"
                :to="{name: 'userProfile'}">
                <i class="ik ik-user" /><span>Meus dados</span>
              </router-link>
              <router-link
                class="dropdown-item"
                title="Alterar senha"
                :to="{name: 'changePassword'}">
                <i class="ik ik-edit" /><span>Alterar senha</span>
              </router-link>
              <router-link
                class="dropdown-item"
                title="Solicitar personificação"
                :to="{name: 'impersonationRequest'}">
                <i class="ik ik-user-check" /><span>Solicitar personificação</span>
              </router-link>
              <router-link
                v-if="isImpersonationAllowed"
                class="dropdown-item"
                title="Personificar"
                :to="{name: 'impersonationValidate'}">
                <i class="ik ik-users" /><span>Personificar</span>
              </router-link>
              <template v-if="isAdmin">
                <div class="dropdown-divider" />
                <h6 class="dropdown-header">Financeiro</h6>
                <router-link
                  class="dropdown-item"
                  title="Histórico de Cobranças"
                  :to="{name: 'chargeHistory'}">
                  <i class="ik ik-credit-card" /><span>Histórico de Cobranças</span>
                </router-link>
                <router-link
                  class="dropdown-item"
                  title="Recarga Automática"
                  :to="{name: 'automaticRecharge'}">
                  <i class="ik ik-refresh-cw" /><span>Recarga Automática</span>
                </router-link>
                <router-link
                  class="dropdown-item"
                  title="Dados Financeiros"
                  :to="{name: 'financialData'}">
                  <i class="ik ik-dollar-sign" /><span>Dados Financeiros</span>
                </router-link>
                <div class="dropdown-divider" />
                <router-link
                  class="dropdown-item"
                  title="Dados da Organização"
                  :to="{name: 'organizationProfile'}">
                  <i class="ik ik-briefcase" /><span>Dados da Organização</span>
                </router-link>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import loopback from '../../services/loopback'
import referralProgramService from '@/services/referral-program'
import ChargeBalance from '@/components/Charge/Balance.vue'
import ChargeNew from '@/components/Charge/New.vue'

const DEFAULT_REFERRAL_ICON = 'ik-gift'
const HTTPS_LINK_PATTERN = /^https:\/\//i

export default {
  name: 'MenuHeader',
  components: {
    ChargeBalance,
    ChargeNew
  },
  data () {
    return {
      isSidebarHovered: false,
      isSidebarCollapsed: false,
      isImpersonationAllowed: false,
      referral: null
    }
  },
  created () {
    this.$root.$on('sidebar-hover', (isHovered) => {
      this.isSidebarHovered = isHovered
    })
    this.$root.$on('sidebar-toggle', (isCollapsed) => {
      this.isSidebarCollapsed = isCollapsed
    })
  },
  mounted () {
    this.loadImpersonationRollout()
    this.loadReferralProgram()
  },
  methods: {
    // O @click.stop impede que o clique chegue no <html>, onde existe um handler
    // que fecha o menu ao clicar fora dele — sem isso, abrir e fechar brigariam
    // no mesmo clique.
    onMobileNavToggle () {
      this.$root.$emit('mobile-nav-toggle')
    },
    loadImpersonationRollout () {
      if (this.isOnboarding) return
      loopback
        .get('/rollout?featureFlag=account-impersonation')
        .then(rollout => {
          this.isImpersonationAllowed = rollout === 'ALLOWED'
        })
        .catch(error => {
          console.error(error)
        })
    },
    loadReferralProgram () {
      if (this.isOnboarding) return
      referralProgramService
        .getActiveSetting()
        .then(result => {
          this.referral = result.success ? result.data : null
        })
    }
  },
  computed: {
    referralLink () {
      if (this.referral === null) return null
      if (!HTTPS_LINK_PATTERN.test(this.referral.link)) return null
      return this.referral.utmPortal
        ? `${this.referral.link}${this.referral.utmPortal}`
        : this.referral.link
    },
    referralIconClass () {
      if (this.referral === null) return DEFAULT_REFERRAL_ICON
      if (this.referral.iconName) return this.referral.iconName
      return DEFAULT_REFERRAL_ICON
    },
    userName () {
      const user = this.$store.state.auth.user
      return user && user.name
    },
    organizationName () {
      const organization = this.$store.state.auth.organization
      return organization && organization.name
    },
    accessType () {
      return this.isAdmin ? 'Administrador' : 'Usuário'
    },
    isAdmin () {
      return this.$store.state.auth.user && this.$store.state.auth.user.role === 'ADMIN'
    },
    isUnlimited () {
      return this.$store.state.auth && this.$store.state.auth.organization && this.$store.state.auth.organization.isUnlimited === true
    },
    isOnboarding () {
      return this.$route && this.$route.meta && this.$route.meta.onboardingLayout === true
    },
    isImpersonating () {
      const impersonation = this.$store.state.auth.impersonation
      return impersonation !== null && impersonation !== undefined && impersonation.active === true
    }
  }
}
</script>

<style lang="scss" scoped>
// O hamburguer nao tinha icone: a regra que desenhava as tres barras esta
// aninhada em .top-menu no _main.scss, e este botao nunca esteve la dentro.
// Sobrava o <button> cru — um circulo cinza do browser, sem nada. Agora usa o
// mesmo icon-set do resto do portal.
.mobile-nav-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  border: none;
  color: #fff;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover,
  &:focus {
    background-color: rgba(255, 255, 255, 0.12);
  }
}
.charge-balance {
  color: var(--white);
}
// Texto, cores, icone e link vem de referral_program_settings (pbxapi), via
// /referral-program/active. As cores abaixo sao o padrao para quando a config
// nao define backgroundColor/textColor — o :style do banco sobrescreve.
.btn-indique-ganhe {
  background-color: #f1b708;
  color: #212529;
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.5;
  text-decoration: none;
  transition: opacity 0.2s;
  &:hover {
    color: #212529;
    opacity: 0.9;
  }
  i {
    margin-right: 6px;
    font-size: 1em;
  }
}
.logged-user {
  color: var(--white);
  font-size: 14px;
  .text-info {
    margin-left: 10px;
  }
}
.user-menu {
  .logged-user {
    cursor: pointer;
    text-decoration: none;
    &:hover,
    &:focus {
      text-decoration: none;
      color: var(--white);
    }
  }
  // Nomes longos não podem empurrar o saldo e os botões do header.
  &__name {
    display: inline-block;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
  }
  &__caret {
    margin-left: 8px;
    flex-shrink: 0;
    font-size: 16px;
    vertical-align: middle;
    transition: transform 0.2s ease-in-out;
  }
  &.show &__caret {
    transform: rotate(180deg);
  }
  &__list {
    min-width: 240px;
    max-width: 280px;
    padding: 6px 0;
    .user-menu__identity {
      display: flex;
      flex-direction: column;
      padding: 10px 16px;
      overflow: hidden;
      b {
        font-size: 14px;
        color: #313131;
        line-height: 1.3;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      small {
        display: flex;
        align-items: baseline;
        gap: 4px;
        font-size: 12px;
        color: #8a94a6;
      }
      // A organização cede espaço; o nível de acesso nunca é truncado.
      .user-menu__org {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .user-menu__role {
        flex-shrink: 0;
        white-space: nowrap;
      }
    }
    .dropdown-header {
      padding: 10px 16px 4px 16px;
      margin: 0;
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: #8a94a6;
    }
    .dropdown-divider {
      margin: 6px 0 0 0;
    }
    .dropdown-item {
      display: flex;
      align-items: center;
      padding: 10px 16px;
      font-size: 13px;
      color: #545454;
      i {
        font-size: 18px;
        margin-right: 10px;
        color: #8a94a6;
      }
      &:hover,
      &:focus,
      &.router-link-active {
        background-color: #f3f3f3;
        color: #0b77eb;
        i {
          color: #0b77eb;
        }
      }
    }
  }
}

.header-top {
  transition: padding 0.3s ease;
  &.nav-collapsed.nav-expanded {
    padding-left: clamp(230px, 13vw, 250px);
  }
}

// Personificação é a única exceção em que papel/organização precisam ser barulhentos:
// é sinal de segurança, não metadado de identidade.
.impersonation-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: #f5a623;
  color: #3d2c00;
  font-size: 13px;
  line-height: 1.3;
  text-align: center;
  i {
    font-size: 16px;
    flex-shrink: 0;
  }
}

.header-top--onboarding {
  background-color: #1f2a3c;
  color: #ffffff;
  padding: 14px 0;

  .onboarding-logo {
    height: 28px;

    &--icon {
      margin-right: 8px;
    }
  }

  .logged-user {
    color: #ffffff;

    .text-info {
      color: rgba(255, 255, 255, 0.6);
    }
  }
}

</style>



// WEBPACK FOOTER //
// src/components/Container/MenuHeader.vue